import * as braze from '@braze/web-sdk';
import { readMeta, readMetaBoolean } from './page';

let brazeInitialized = false;
const brazeLoadedEvent = new Event('brazeLoaded');

interface BrazeConfig {
  apiKey: string;
  baseUrl: string;
  enableLogging: boolean;
  accountId: string;
  nonceScript: HTMLScriptElement;
}

function getBrazeConfig(): BrazeConfig | null {
  const apiKey = readMeta('braze_api_key');
  const baseUrl = readMeta('braze_base_url');
  const accountId = readMeta('account', 'data-account');
  const nonceScript = document.querySelector('script[nonce]') as HTMLScriptElement | null;

  if (!apiKey || !baseUrl || !accountId || !nonceScript) {
    return null;
  }

  return {
    apiKey,
    baseUrl,
    enableLogging: readMetaBoolean('braze_enable_logging') || false,
    accountId,
    nonceScript,
  };
}

function initializeBraze(config: BrazeConfig): void {
  braze.initialize(config.apiKey, {
    baseUrl: config.baseUrl,
    enableLogging: config.enableLogging,
    allowUserSuppliedJavascript: true,
    contentSecurityNonce: config.nonceScript.nonce,
  });

  braze.automaticallyShowInAppMessages();
  braze.changeUser(config.accountId);
  braze.openSession();

  // Make Braze SDK available globally for GTM and other external scripts
  (window as any).braze = braze;

  brazeInitialized = true;
  document.dispatchEvent(brazeLoadedEvent);
}

export function setupBraze(): void {
  const config = getBrazeConfig();

  if (config) {
    initializeBraze(config);
  } else {
    console.warn('Braze configuration incomplete - skipping initialization');
  }
}

function brazeHasLoaded(): boolean {
  if (!brazeInitialized) {
    console.warn('Braze has not been initialized');
    return false;
  }
  return true;
}

export function sendBrazeEvent(eventName: string): void {
  if (brazeHasLoaded()) {
    braze.logCustomEvent(eventName);
  } else {
    // Queue the event to be sent once Braze is loaded
    const sendEvent = () => {
      braze.logCustomEvent(eventName);
      document.removeEventListener('brazeLoaded', sendEvent);
    };
    document.addEventListener('brazeLoaded', sendEvent);
  }
}
